import { useLayoutEffect, useRef, useState } from 'react';
import { Layers, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ClampText } from '@/components/ui/clamp-text';
import { cn } from '@/lib/utils';
import { NODE_HUE, nodeTypeSurface } from '@/lib/node-visuals';
import { TILE_CAP, meterProportion, type BandType } from '@/lib/synthesis-board';
import type { SynthesisAssignment } from '@/types/api';
import { MemberTile } from './MemberTile';

// One cluster container. The frame is deliberately *recessed* relative to its
// own tiles: --background is 93% lightness and --card is 98%, so a 6% hue mix
// on the page ground sits under the bg-card tiles, reading as a tray holding
// notes rather than a card stacked on a card.
export function ClusterGroup({
  variant,
  type,
  title,
  description,
  respondentCount,
  respondentTotal,
  itemCount,
  memberIds,
  labelOf,
  excluded,
  included,
  onToggleMember,
  onTitleChange,
  onPromote,
  onReject,
  merging,
  onToggleMerge,
  pending = [],
  onAcceptAssignment,
  onRejectAssignment,
  canEdit,
  busy,
}: {
  variant: 'proposed' | 'anchored';
  type: BandType;
  title: string;
  description?: string;
  respondentCount: number;
  respondentTotal?: number;
  itemCount: number;
  memberIds: string[];
  labelOf: (id: string) => string;
  // This container's own unticked member ids, and the members a promote from
  // here should send. Both are derived by the board: it holds one exclusion set
  // for the whole page, keyed by theme as well as finding, and includedMembers
  // is the tested rule that turns it into a promote payload.
  excluded: ReadonlySet<string>;
  included: string[];
  onToggleMember: (id: string) => void;
  onTitleChange?: (t: string) => void;
  onPromote?: (memberItemIds: string[]) => void;
  onReject?: () => void;
  merging?: boolean;
  onToggleMerge?: () => void;
  pending?: SynthesisAssignment[];
  onAcceptAssignment?: (a: SynthesisAssignment) => void;
  onRejectAssignment?: (a: SynthesisAssignment) => void;
  canEdit: boolean;
  busy: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const proposed = variant === 'proposed';
  const hue = NODE_HUE[type];
  const proportion = meterProportion(respondentCount, respondentTotal);
  const hiddenCount = expanded ? 0 : Math.max(0, memberIds.length - TILE_CAP);
  const shown = expanded ? memberIds : memberIds.slice(0, TILE_CAP);
  const someExcluded = proposed && included.length < memberIds.length;

  // Auto-grow the title textarea to fit its content. Mutating style.height
  // directly (no setState) keeps this effect free of the react-hooks
  // "avoid derived state in effects" warning.
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [title]);

  return (
    <div
      style={nodeTypeSurface(hue, { mix: 6, borderMix: 28, ground: 'hsl(var(--background))' })}
      className={cn(
        'rounded-lg border p-2.5',
        !proposed && 'opacity-80',
        merging && 'ring-2 ring-foreground/25',
      )}
    >
      {proportion !== null ? (
        <div
          className="mb-1.5 h-1 overflow-hidden rounded-full"
          style={{ background: `color-mix(in srgb, ${hue} 12%, transparent)` }}
        >
          <div
            className="h-full"
            style={{
              width: `${proportion * 100}%`,
              background: `color-mix(in srgb, ${hue} 55%, transparent)`,
            }}
          />
        </div>
      ) : null}

      <div className="mb-2 flex flex-col gap-1">
        {proposed && onTitleChange ? (
          <textarea
            ref={titleRef}
            rows={1}
            value={title}
            aria-label="Theme title"
            maxLength={2000}
            readOnly={!canEdit}
            onChange={(e) => onTitleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            className="w-full resize-none overflow-hidden rounded bg-transparent px-1 text-sm font-medium leading-snug focus:outline-hidden focus:ring-2 focus:ring-ring"
          />
        ) : (
          <span className="w-full text-sm font-medium leading-snug">{title}</span>
        )}
        <div className="flex items-center gap-2">
          <span className="flex-1 text-[11px] tabular-nums text-muted-foreground">
            {proportion !== null
              ? `${respondentCount} of ${respondentTotal}`
              : `${itemCount} finding${itemCount === 1 ? '' : 's'}`}
          </span>
          {proposed && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2 text-[11px]"
                onClick={onToggleMerge}
                disabled={!canEdit || busy}
                aria-pressed={!!merging}
                aria-label="Select for merge"
              >
                Merge
              </Button>
              {/* Outline, not solid pine: the view already has one brand-gradient
                  CTA, and up to 15 filled Promote buttons would outshout it. */}
              <Button
                variant="outline"
                size="sm"
                className="h-7 shrink-0"
                onClick={() => onPromote?.(included)}
                disabled={!canEdit || busy || included.length === 0 || !title.trim()}
              >
                <Layers className="mr-1.5 h-3.5 w-3.5" />
                {someExcluded ? `Promote ${included.length} of ${memberIds.length}` : 'Promote'}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={onReject}
                disabled={!canEdit || busy}
                aria-label="Reject theme"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {shown.map((id) => (
          <MemberTile
            key={id}
            title={labelOf(id)}
            mode={proposed ? 'selectable' : 'static'}
            checked={!excluded.has(id)}
            onToggle={() => onToggleMember(id)}
            disabled={!canEdit || busy}
          />
        ))}
        {pending.map((a) => (
          <MemberTile
            key={a.id}
            title={labelOf(a.itemId)}
            mode="pending"
            onAccept={() => onAcceptAssignment?.(a)}
            onReject={() => onRejectAssignment?.(a)}
            disabled={!canEdit || busy}
          />
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="self-center px-1 text-[11px] text-primary underline"
          >
            +{hiddenCount} more
          </button>
        )}
      </div>

      {/* This is the model's own account of why these findings belong together.
          It gets persisted onto the node on promote and is what the deck-generation
          prompt reads, so it needs to be readable before that click, not only in
          the node details modal afterwards. Anchored themes skip this: the modal
          already shows it there, and the decision to promote is already made. */}
      {proposed && description && (
        <ClampText text={description} lines={2} className="mt-2 text-[10.5px] text-muted-foreground" />
      )}

      {someExcluded && (
        <p className="mt-2 text-[10.5px] text-muted-foreground">
          Unticked findings stay on your graph, just not attached to this theme.
        </p>
      )}
    </div>
  );
}
